import React from 'react';
import { TableRow, TableCell, Checkbox, IconButton, Typography, Chip, Box } from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import useCurrency from '../../../hooks/common/useCurrency';
import CouponEffectiveStatusBadge, { resolveEffectiveStatus } from './CouponEffectiveStatusBadge';

const CouponsTableRow = ({
    coupon,
    index,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { format: formatPrice } = useCurrency();

    const getTypeLabel = (type) => {
        if (type === 'percent') {
            return isArabic ? 'نسبة مئوية' : 'Percentage';
        }
        return isArabic ? 'مبلغ ثابت' : 'Fixed';
    };

    const formatValue = (type, value) => {
        if (type === 'percent') {
            return `${value}%`;
        }
        return formatPrice(value);
    };

    const status = resolveEffectiveStatus(coupon);
    const isExpired = status === 'expired';

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isSelected}
                    onClick={(event) => onRowCheckboxClick(event, coupon.id)}
                />
            </TableCell>

            {/* S/N */}
            <TableCell align="start">{index + 1}</TableCell>

            {/* Code */}
            <TableCell>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
                    {coupon.code}
                </Typography>
            </TableCell>

            {/* Type */}
            <TableCell align="center">
                <Chip
                    label={getTypeLabel(coupon.type)}
                    size="small"
                    variant="tonal"
                    color={coupon.type === 'percent' ? 'primary' : 'secondary'}
                    sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                />
            </TableCell>

            {/* Value */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                    {formatValue(coupon.type, coupon.value)}
                </Typography>
            </TableCell>

            {/* Min Order */}
            <TableCell align="center">
                <Typography variant="body2">
                    {coupon.min_order_total ? formatPrice(coupon.min_order_total) : '-'}
                </Typography>
            </TableCell>

            {/* Used Count */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="medium">
                    {coupon.redemption_count || 0}
                </Typography>
            </TableCell>

            {/* Status */}
            <TableCell align="center">
                <CouponEffectiveStatusBadge status={status} />
            </TableCell>

            {/* Ends At */}
            <TableCell align="center">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" color={isExpired ? 'error' : 'text.primary'} fontWeight={isExpired ? 'bold' : 'normal'} sx={{ whiteSpace: 'nowrap' }}>
                        {coupon.ends_at ? dayjs(coupon.ends_at).format('YYYY-MM-DD') : '-'}
                    </Typography>
                    {isExpired && (
                        <Typography variant="caption" color="error" sx={{ fontSize: '10px', fontWeight: 'bold' }}>
                            {isArabic ? "منتهي" : "EXPIRED"}
                        </Typography>
                    )}
                </Box>
            </TableCell>

            {/* Actions */}
            <TableCell align="center">
                <IconButton
                    size="small"
                    onClick={(e) => onMenuOpen(e, coupon)}
                >
                    <MoreIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default CouponsTableRow;
